/**
 * The placeholder registry and pure template renderer for automation email
 * copy. Admins write subject and body copy with `{{key}}` tokens, inserted as
 * chips in the builder; the executor resolves each token to a concrete value at
 * send time and calls {@link renderAutomationTemplate} to substitute them.
 *
 * The split is deliberate: the resolver that builds the `values` map does IO
 * (loads the participant, the session, the sender's display name) and lives
 * with the executor. This module is pure: it declares which tokens exist and
 * substitutes them, and it never throws, so a stray or future token can never
 * fail a send.
 *
 * @module lib/api-contracts/automation/placeholders
 */

/** One admin-facing placeholder token. */
export interface AutomationPlaceholder {
  /** The token key, rendered as `{{key}}` in copy. */
  key: string;
  /** Short human label shown on the insert chip. */
  label: string;
  /** One-line explanation of what the token resolves to. */
  description: string;
  /** Value used when the resolved participant data is empty. */
  defaultValue: string;
  /**
   * Legacy metadata retained for stored consumers. All current placeholders
   * are available regardless of reply routing.
   */
  schedulingOnly: boolean;
}

/**
 * The participant-safe tokens an admin can insert into automation copy. Order is the order
 * the insert menu lists them.
 */
export const AUTOMATION_PLACEHOLDERS: AutomationPlaceholder[] = [
  {
    key: 'participant_first_name',
    label: 'First name',
    description: "The participant's first name, or a friendly fallback when unknown.",
    defaultValue: 'there',
    schedulingOnly: false,
  },
  {
    key: 'participant_full_name',
    label: 'Full name',
    description: "The participant's full name, or a friendly fallback when unknown.",
    defaultValue: 'there',
    schedulingOnly: false,
  },
  {
    key: 'company',
    label: 'Company',
    description: "The participant's company, or empty when unknown.",
    defaultValue: '',
    schedulingOnly: false,
  },
  {
    key: 'sender_name',
    label: 'Sender name',
    description: 'The display name of the org member sending the email.',
    defaultValue: '',
    schedulingOnly: false,
  },
  {
    key: 'estimated_duration',
    label: 'Estimated duration',
    description: 'How long the conversation is expected to take.',
    defaultValue: '',
    schedulingOnly: false,
  },
  {
    key: 'conversation_link',
    label: 'Conversation link',
    description: "The participant's personalized start-or-resume link.",
    defaultValue: '',
    schedulingOnly: false,
  },
  {
    key: 'closing_date',
    label: 'Closing date',
    description: "The campaign's closing date in the participant's timezone, or empty.",
    defaultValue: '',
    schedulingOnly: false,
  },
  {
    key: 'suggested_times',
    label: 'Suggested times',
    description: 'Proposed conversation times.',
    defaultValue: '',
    schedulingOnly: false,
  },
  {
    key: 'unsubscribe',
    label: 'Unsubscribe',
    description: "The participant's authenticated unsubscribe link.",
    defaultValue: '',
    schedulingOnly: false,
  },
];

/** The known token keys, for O(1) membership checks during rendering. */
const KNOWN_KEYS = new Set(AUTOMATION_PLACEHOLDERS.map((p) => p.key));

/** Registered default values keyed for rendering and the sequence editor. */
const DEFAULT_BY_KEY = new Map(
  AUTOMATION_PLACEHOLDERS.map((placeholder) => [placeholder.key, placeholder.defaultValue]),
);

/** Matches `{{key}}` and `{{key|default}}` tokens with optional whitespace. */
const TOKEN_RE = /\{\{\s*([a-z0-9_]+)(?:\|([^{}|]*))?\s*\}\}/gi;
const LEGACY_CAMPAIGN_PURPOSE_RE = /\{\{\s*campaign_purpose\s*\}\}/i;
const LEGACY_CAMPAIGN_NAME_RE = /\{\{\s*campaign_name(?:\|[^{}|]*)?\s*\}\}/i;
const ESTIMATED_DURATION_RE = /\{\{\s*estimated_duration(?:\|[^{}|]*)?\s*\}\}/i;

/** Return the registered default for a known placeholder key. */
export function automationPlaceholderDefault(key: string): string {
  return DEFAULT_BY_KEY.get(key) ?? '';
}

/** Resolve a token's explicit default, falling back to its registry value. */
function tokenDefault(key: string, inlineDefault: string | undefined): string {
  return inlineDefault === undefined
    ? automationPlaceholderDefault(key)
    : inlineDefault.trim();
}

/** True when a token has a real value or a non-empty saved default. */
function hasRenderableTokenValue(
  template: string,
  key: string,
  values: Record<string, string>,
): boolean {
  if (values[key]?.trim()) return true;
  TOKEN_RE.lastIndex = 0;
  for (const match of template.matchAll(TOKEN_RE)) {
    if (match[1].toLowerCase() !== key) continue;
    if (tokenDefault(key, match[2]).length > 0) return true;
  }
  return false;
}

/** Remove the sentence containing a token whose trusted value is unavailable. */
function removeTokenSentence(template: string, tokenPattern: RegExp): string {
  let result = template;
  let match = tokenPattern.exec(result);

  while (match) {
    const tokenStart = match.index;
    const tokenEnd = tokenStart + match[0].length;
    const sentenceStart = Math.max(
      result.lastIndexOf('.', tokenStart - 1),
      result.lastIndexOf('!', tokenStart - 1),
      result.lastIndexOf('?', tokenStart - 1),
      result.lastIndexOf('\n', tokenStart - 1),
      result.lastIndexOf('\r', tokenStart - 1),
    ) + 1;
    const nextBoundary = result.slice(tokenEnd).search(/[.!?\r\n]/u);
    const boundaryIndex = nextBoundary === -1 ? result.length : tokenEnd + nextBoundary;
    const boundary = result[boundaryIndex] ?? '';
    const sentenceEnd = /[.!?]/u.test(boundary) ? boundaryIndex + 1 : boundaryIndex;
    result = `${result.slice(0, sentenceStart)}${result.slice(sentenceEnd)}`;
    match = tokenPattern.exec(result);
  }

  return result
    .replace(/[ \t]+\n/gu, '\n')
    .replace(/\n{3,}/gu, '\n\n')
    .trim();
}

function replaceLegacyCampaignPurpose(
  template: string,
  values: Record<string, string>,
): string {
  if (!LEGACY_CAMPAIGN_PURPOSE_RE.test(template)) return template;

  const safeSummary = values.participant_safe_campaign_summary?.trim();
  if (safeSummary) {
    return template.replace(/\{\{\s*campaign_purpose\s*\}\}/gi, safeSummary);
  }

  let result = template;
  let match = LEGACY_CAMPAIGN_PURPOSE_RE.exec(result);

  while (match) {
    const tokenStart = match.index;
    const tokenEnd = tokenStart + match[0].length;
    const sentenceStart = Math.max(
      result.lastIndexOf('.', tokenStart - 1),
      result.lastIndexOf('!', tokenStart - 1),
      result.lastIndexOf('?', tokenStart - 1),
      result.lastIndexOf('\n', tokenStart - 1),
      result.lastIndexOf('\r', tokenStart - 1),
    ) + 1;
    const nextBoundary = result.slice(tokenEnd).search(/[.!?\r\n]/);
    const boundaryIndex = nextBoundary === -1 ? result.length : tokenEnd + nextBoundary;
    const boundary = result[boundaryIndex] ?? '';
    const sentenceEnd = /[.!?]/.test(boundary) ? boundaryIndex + 1 : boundaryIndex;
    result = `${result.slice(0, sentenceStart)}${result.slice(sentenceEnd)}`;
    match = LEGACY_CAMPAIGN_PURPOSE_RE.exec(result);
  }

  return result.trim();
}

/**
 * Substitutes `{{key}}` tokens in a template. Known tokens are replaced with
 * their supplied value, then their saved inline default, then the registered
 * default. The retired private campaign-title token fails closed by removing
 * its sentence. Other unknown tokens remain verbatim so an admin's typo or a
 * future token never disappears or throws. Pure and total: any string in
 * yields a string out.
 *
 * @param template The copy containing zero or more `{{key}}` tokens.
 * @param values Resolved values keyed by token key.
 * @returns The template with known tokens substituted.
 */
export function renderAutomationTemplate(template: string, values: Record<string, string>): string {
  const privateTitleSafeTemplate = removeTokenSentence(template, LEGACY_CAMPAIGN_NAME_RE);
  const durationSafeTemplate = hasRenderableTokenValue(
    privateTitleSafeTemplate,
    'estimated_duration',
    values,
  )
    ? privateTitleSafeTemplate
    : removeTokenSentence(privateTitleSafeTemplate, ESTIMATED_DURATION_RE);
  const renderedPublicTokens = durationSafeTemplate.replace(
    TOKEN_RE,
    (match, rawKey: string, inlineDefault: string | undefined) => {
      const key = rawKey.toLowerCase();
      if (!KNOWN_KEYS.has(key)) return match;
      const value = values[key];
      return value?.trim() ? value : tokenDefault(key, inlineDefault);
    },
  );
  return replaceLegacyCampaignPurpose(renderedPublicTokens, values);
}
